/**
 * React Query Hook for fetching invoice details
 * GET /api/v1/invoices/{id}/details
 */

import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import { InvoiceDetails, ApiErrorResponse } from '@/types';
import { apiClient } from '@/api/client';

export const INVOICE_DETAILS_QUERY_KEY = 'invoiceDetails';

interface UseInvoiceDetailsOptions extends Omit<UseQueryOptions<InvoiceDetails, ApiErrorResponse>, 'queryKey' | 'queryFn'> {
  invoiceId: number;
}

/**
 * Fetch detailed invoice information including line items, payments, and financial summary
 * @param options Query options including invoiceId
 * @returns Query result with invoice details
 */
export const useInvoiceDetails = ({ invoiceId, ...options }: UseInvoiceDetailsOptions) => {
  return useQuery<InvoiceDetails, ApiErrorResponse>({
    queryKey: [INVOICE_DETAILS_QUERY_KEY, invoiceId],
    queryFn: async () => {
      const response = await apiClient.get<InvoiceDetails>(
        `/commerce-service/api/v1/invoices/${invoiceId}/details`
      );
      return response.data;
    },
    enabled: !!invoiceId && invoiceId > 0,
    staleTime: 30000, // 30 seconds
    gcTime: 300000, // 5 minutes (formerly cacheTime)
    retry: 2,
    ...options,
  });
};

/**
 * Helper function to manually refetch invoice details
 */
export const refetchInvoiceDetails = (queryClient: any, invoiceId: number) => {
  return queryClient.invalidateQueries({
    queryKey: [INVOICE_DETAILS_QUERY_KEY, invoiceId]
  });
};

/**
 * Helper function to prefetch invoice details (useful for list hover)
 */
export const prefetchInvoiceDetails = async (
  queryClient: any,
  invoiceId: number
) => {
  await queryClient.prefetchQuery({
    queryKey: [INVOICE_DETAILS_QUERY_KEY, invoiceId],
    queryFn: async () => {
      const response = await apiClient.get<InvoiceDetails>(
        `/commerce-service/api/v1/invoices/${invoiceId}/details`
      );
      return response.data;
    },
    staleTime: 30000,
  });
};
